/**
 * Design tokens — the machine-readable half of docs/02-STYLE-BIBLE.md (v2.0).
 *
 * The direction is a 1980s-90s PRINTED board game: flat spot inks, a heavy black
 * key line, halftone dots instead of shading, and colour plates that sit slightly
 * off the key line the way cheap offset printing did.
 *
 * There are no gradients and no lighting in this system. If something needs to be
 * darker, it gets a denser halftone, not a mixed colour.
 */

/** Style Bible §1.1 — the cardstock itself. */
export const PAPER = {
  board: '#F2E8D0',
  card: '#F7F0DF',
  aged: '#E3D4B0',
  shadow: '#C9B88A',
  box: '#D8C9A6',
} as const;

/** Style Bible §1.2 — the black plate. Never pure #000; ink is not that black. */
export const INK = {
  key: '#1A1714',
  text: '#241F1A',
  faded: '#6B6156',
} as const;

/**
 * Style Bible §1.3 — six spot inks and nothing else. A press of this era ran a
 * limited number of plates, and holding to that is what makes the look read as
 * printed rather than as a flat-design pastiche.
 */
export const SPOT = {
  red: '#D8402F',
  blue: '#2B6CB0',
  yellow: '#F2C230',
  green: '#3E9B57',
  purple: '#7B4C9E',
  orange: '#E8802B',
} as const;

export type SpotColor = keyof typeof SPOT;

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';

export type FrameStyle = 'single' | 'notched' | 'double' | 'screened' | 'starred' | 'misregistered';

/** Style Bible §1.4 */
export const RARITY: Readonly<Record<Rarity, { color: string; frame: FrameStyle }>> = {
  common: { color: INK.faded, frame: 'single' },
  uncommon: { color: SPOT.green, frame: 'notched' },
  rare: { color: SPOT.blue, frame: 'double' },
  epic: { color: SPOT.purple, frame: 'screened' },
  legendary: { color: SPOT.yellow, frame: 'starred' },
  mythic: { color: SPOT.red, frame: 'misregistered' },
};

/**
 * Style Bible §5.1 — a class prints in ONE spot ink, and it is the ink whose
 * meaning in §1.3 already matches the class.
 *
 * This lives in tokens rather than in each renderer because it did drift: the
 * board pawn printed a rogue in purple and a mage in blue while the portrait
 * printed the reverse, so the piece on the board was not the person on the card.
 */
export type PortraitRole = 'warrior' | 'rogue' | 'mage' | 'priest' | 'npc';

export const ROLE_INK: Readonly<Record<PortraitRole, string>> = {
  warrior: SPOT.red,
  rogue: SPOT.blue,
  mage: SPOT.purple,
  priest: SPOT.yellow,
  npc: SPOT.green,
};

export type RegionId =
  | 'aurelia'
  | 'elden'
  | 'ironMountains'
  | 'redDesert'
  | 'blackCoast'
  | 'ashenland'
  | 'frostland';

/**
 * Style Bible §1.5 — a region picks ONE of the six inks. It does not get a
 * palette of its own, which is what stops seven regions becoming seven styles.
 */
export const REGION_INK: Readonly<Record<RegionId, string>> = {
  aurelia: SPOT.yellow,
  elden: SPOT.green,
  ironMountains: INK.faded,
  redDesert: SPOT.orange,
  blackCoast: SPOT.blue,
  ashenland: INK.faded,
  frostland: SPOT.blue,
};

/** Style Bible §2.1 — three weights, at a 1024px reference canvas. */
export const LINE = { outline: 4.0, structure: 2.2, detail: 1.2 } as const;

/** Style Bible §2.2 — halftone densities. Shading is dot size, never a gradient. */
export const HALFTONE = {
  light: { dot: 1.1, grid: 6 },
  mid: { dot: 1.8, grid: 6 },
  dark: { dot: 2.6, grid: 6 },
  shadow: { dot: 3.4, grid: 6 },
} as const;

export type HalftoneDensity = keyof typeof HALFTONE;

/**
 * Style Bible §2.2 — one screen angle per plate.
 *
 * Two screens printed at the SAME angle put their dots in exactly the same place,
 * so the second plate hides under the first and adds no density at all. That is
 * not a theoretical concern: face shading printed at 45° over a 45° skin screen
 * was invisible on screen until these angles were split apart.
 */
export const SCREEN_ANGLE = { key: 45, spot: 75, spotAlt: 15 } as const;

/** The black plate's angle. Kept as the default for single-screen artwork. */
export const HALFTONE_ANGLE = SCREEN_ANGLE.key;

/** Style Bible §2.3 — colour-plate offset, as a fraction of canvas size. */
export const MISREGISTRATION = 0.0035;

/** Style Bible §2.4 — cut cardstock corners. */
export const PAPER_RADIUS = 12;

export const DICE_SPEC = {
  canvas: 256,
  cornerRadiusRatio: 0.16,
  pipDiameterRatio: 0.18,
} as const;

/** Style Bible §4 — cards keep the 5:7 print ratio. */
export const CARD_SPEC = {
  width: 750,
  height: 1050,
  radius: 20,
  bleed: 24,
  frameInset: 34,
  artwork: { x: 34, y: 92, w: 682, h: 560 },
  titlePlate: { h: 86 },
  bodyPlate: { h: 252 },
  cornerIcon: 34,
} as const;

export const TILE_SPEC = { size: 512, isoRatio: 0.5 } as const;
export const PORTRAIT_SPEC = { size: 512 } as const;
export const ICON_SPEC = { size: 128 } as const;

export type DiceSkin =
  | 'wooden'
  | 'iron'
  | 'ancient'
  | 'fate'
  | 'cursed'
  | 'golden'
  | 'ghost'
  | 'dragon';

export interface DiceSkinSpec {
  readonly body: string;
  readonly pip: string;
  /** Adds a halftone screen over the body, for the aged ivory look. */
  readonly screened: boolean;
  readonly label: string;
}

/**
 * Style Bible §3. A 1980s board game die is ivory plastic with black pips.
 *
 * Note what is absent: any modifier, weight or luck field. A pay-to-win die is
 * not expressible in this type, which is how CLAUDE.md §4.6 is enforced.
 */
export const DICE_SKINS: Readonly<Record<DiceSkin, DiceSkinSpec>> = {
  wooden: { body: '#E8D9B8', pip: INK.key, screened: false, label: '상아' },
  iron: { body: '#B9BDC0', pip: INK.key, screened: false, label: '회색' },
  ancient: { body: PAPER.box, pip: INK.key, screened: true, label: '오래된' },
  fate: { body: SPOT.purple, pip: PAPER.card, screened: false, label: '운명' },
  cursed: { body: SPOT.red, pip: PAPER.card, screened: false, label: '저주' },
  golden: { body: SPOT.yellow, pip: INK.key, screened: false, label: '황금' },
  ghost: { body: PAPER.card, pip: INK.faded, screened: true, label: '유령' },
  dragon: { body: SPOT.orange, pip: INK.key, screened: false, label: '용' },
};

export type CardType =
  | 'EVENT'
  | 'ACTION'
  | 'FATE'
  | 'KNOWLEDGE'
  | 'MEMORY'
  | 'RELATIONSHIP'
  | 'CURSE'
  | 'LEGEND';

/** Card types differ by spot ink and glyph only. */
export const CARD_TYPE_INK: Readonly<Record<CardType, string>> = {
  EVENT: SPOT.blue,
  ACTION: SPOT.green,
  FATE: SPOT.purple,
  KNOWLEDGE: SPOT.blue,
  MEMORY: INK.faded,
  RELATIONSHIP: SPOT.orange,
  CURSE: SPOT.red,
  LEGEND: SPOT.yellow,
};

// ---------------------------------------------------------------------------
// Colour utilities
// ---------------------------------------------------------------------------

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const to = (v: number): string =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

export function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

export function hslToRgb({ h, s, l }: { h: number; s: number; l: number }): Rgb {
  const hn = (((h % 360) + 360) % 360) / 360;
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return { r: channel(hn + 1 / 3) * 255, g: channel(hn) * 255, b: channel(hn - 1 / 3) * 255 };
}

/**
 * Nudge lightness. Used sparingly — the print look wants flat ink, so this is for
 * paper tones and plate edges, not for shading artwork.
 */
export function shade(hex: string, amount: number): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb({ ...hsl, l: Math.max(0, Math.min(1, hsl.l + amount)) }));
}

/** The single spot ink a region prints in (Style Bible §1.5). */
export function regionInk(region: RegionId): string {
  return REGION_INK[region];
}

export const ALL_SPOTS: readonly string[] = Object.values(SPOT);
